import clsx from 'clsx';
import { CHART_SEMANTIC, CHART_SERIES, seriesColor } from './palette';

export interface BarListItem {
  label: string;
  value: number;
  /** Overrides the colour this row would otherwise get from `colorMode`. */
  color?: string;
  /** Renders the label in full weight — for "our brand" among competitors. */
  highlight?: boolean;
  /** Muted text after the value, e.g. a deal count beside a euro amount. */
  note?: string;
}

interface BarListChartProps {
  items: BarListItem[];
  /** Formats the trailing value and the axis ticks. Defaults to a plain integer. */
  formatValue?: (value: number) => string;
  /** Bar scale ceiling. Defaults to the largest item value. */
  max?: number;
  /**
   * `series` gives each row the next categorical colour (matches the product's
   * dot-legend charts). `uniform` paints every bar the same, so length alone
   * carries the comparison. `highlight` mutes everything except highlighted rows.
   */
  colorMode?: 'series' | 'uniform' | 'highlight';
  /** Colour used by `uniform`, and for highlighted rows in `highlight` mode. */
  color?: string;
  /** Tailwind width class for the label column. */
  labelWidth?: string;
  /** Tailwind width class for the trailing value column. Widen it for currency. */
  valueWidth?: string;
  /** Show the numeric value at the end of each row. */
  showValues?: boolean;
  /** Draw a faint track behind each bar. Off by default, matching the product. */
  showTrack?: boolean;
  /** Number of ticks on the shared bottom axis. 0 hides the axis. */
  axisTicks?: number;
  className?: string;
}

/**
 * Horizontal bars with a left-aligned dot legend and a shared value axis — the
 * product dashboard's signature panel (Projects by Status, Opportunity Stage).
 *
 * Deliberately plain DOM rather than recharts: these are ranked category totals,
 * not a continuous plot, and the OS had a dozen hand-rolled `<div style={{width}}>`
 * versions of exactly this.
 */
export function BarListChart({
  items,
  formatValue = (v) => String(Math.round(v)),
  max,
  colorMode = 'series',
  color = CHART_SERIES[0],
  labelWidth = 'w-32',
  valueWidth = 'w-14',
  showValues = true,
  showTrack = false,
  axisTicks = 0,
  className,
}: BarListChartProps) {
  const ceiling = max ?? Math.max(...items.map((i) => i.value), 0);
  const ticks = axisTicks > 0 ? niceTicks(ceiling, axisTicks) : [];
  const scaleMax = ticks.length > 0 ? ticks[ticks.length - 1] : ceiling;

  function barColor(item: BarListItem, index: number): string {
    if (item.color) return item.color;
    if (colorMode === 'uniform') return color;
    if (colorMode === 'highlight') return item.highlight ? color : CHART_SEMANTIC.muted;
    return seriesColor(index);
  }

  return (
    <div className={clsx('space-y-2', className)}>
      {items.map((item, i) => (
        <div key={item.label} className="flex items-center gap-3">
          <div className={clsx('flex items-center gap-2 flex-none', labelWidth)}>
            <span
              className="h-1.5 w-1.5 flex-none rounded-full"
              style={{ backgroundColor: barColor(item, i) }}
              aria-hidden
            />
            <span
              className={clsx(
                'text-body-xs truncate',
                item.highlight ? 'text-text-primary font-medium' : 'text-text-secondary'
              )}
              title={item.label}
            >
              {item.label}
            </span>
          </div>
          <div className={clsx('h-2 flex-1 rounded-sm', showTrack && 'bg-surface-tertiary')}>
            <div
              className="h-full rounded-sm transition-all"
              style={{
                width: scaleMax > 0 ? `${Math.max((item.value / scaleMax) * 100, item.value > 0 ? 1 : 0)}%` : '0%',
                backgroundColor: barColor(item, i),
              }}
            />
          </div>
          {showValues && (
            <span
              className={clsx('text-body-xs flex-none text-right tabular-nums', valueWidth)}
            >
              <span className="text-text-secondary">{formatValue(item.value)}</span>
              {item.note && <span className="text-text-tertiary"> {item.note}</span>}
            </span>
          )}
        </div>
      ))}

      {ticks.length > 0 && (
        <div className="flex items-center gap-3 pt-1">
          <div className={clsx('flex-none', labelWidth)} />
          <div className="relative flex-1 h-4">
            {ticks.map((t, i) => (
              <span
                key={t}
                className="absolute top-0 text-body-xs text-text-muted tabular-nums"
                style={{
                  left: `${(t / scaleMax) * 100}%`,
                  transform: i === 0 ? 'none' : i === ticks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                }}
              >
                {formatValue(t)}
              </span>
            ))}
          </div>
          {showValues && <div className={clsx('flex-none', valueWidth)} />}
        </div>
      )}
    </div>
  );
}

/**
 * Axis ticks at a round step covering `max`, e.g. 12 → [0, 3, 6, 9, 12].
 * Returns [0] when there is nothing to scale, so callers never divide by zero.
 */
function niceTicks(max: number, count: number): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0];
  const rawStep = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rawStep) ?? 10 * magnitude;
  const ticks: number[] = [];
  for (let t = 0; t <= max + step / 2; t += step) {
    ticks.push(Number(t.toFixed(10)));
  }
  return ticks;
}
